import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { motion } from 'framer-motion';
import AudioGraph from './LineChartComponent';
import DonutChart from './Donutchart';
import SemiCircularProgress from './GaugeChart';
import Pitchchart from './Pitchchart';

const pauseColors = ["#FF8042", "#00C49F"];

// Helper component for highlighting text
const HighlightedText = ({ text, fillerWordsIndex }) => {
  if (!text) return <p className="text-gray-600">No transcript available.</p>;

  const words = text.split(/(\s+)/); // Preserve spaces

  const highlightedText = words.map((word, index) => {
    const cleanedWord = word.toLowerCase().replace(/[^a-z0-9]/g, "");
    const isHighlighted = Object.keys(fillerWordsIndex || {}).includes(cleanedWord);

    return (
      <span
        key={index}
        className={`${isHighlighted ? 'font-bold text-red-600' : 'font-normal text-[#303972]'}`}
        style={{ fontFamily: 'sans-serif, Archivo' }}
      >
        {word}
      </span>
    );
  });

  return <p className="text-base leading-relaxed">{highlightedText}</p>;
};

const AudioAnalyticsContent = ({ audioData, textData, mode }) => {
  const [activeTab, setActiveTab] = useState('volume');

  // --- Start of existing data processing (no changes needed here) ---
  const formatValue = (value) => (value ? (value * 100).toFixed(1) + "%" : "0.0%");
  const audioDuration = parseFloat(audioData.Audio_Duration);

  function formatTime(timeInMs) {
    return `${Math.floor(timeInMs / 1000)}s`;
  }

  const volumeDataString = audioData?.V_DP?.[500];
  const volumeDataArray = volumeDataString ? JSON.parse(volumeDataString) : [];

  // Ensure we have valid data before processing
  const transformedVolumeData = Array.isArray(volumeDataArray) 
    ? volumeDataArray
        .filter((_, index) => index % 2 === 0)
        .map((value, index) => ({
          timeMs: index * 1000,
          db: value ?? 0,
        }))
    : [];

  // Add a default data point if array is empty to prevent errors
  if (transformedVolumeData.length === 0) {
    transformedVolumeData.push({
      timeMs: 0,
      db: 0
    });
  }

  if (!volumeDataString || transformedVolumeData.length === 0) {
    console.warn("V_DP data is missing or invalid.");
  }

  const totalDurationMs = transformedVolumeData.length * 1000;
  const intervalMs = 1000;

  const intervals = [];
  for (let time = 0; time <= totalDurationMs; time += intervalMs) {
    intervals.push(time);
  }

  const filteredVolumeData = intervals.map((targetTime) => {
    if (!transformedVolumeData || transformedVolumeData.length === 0) {
      return { timeMs: targetTime, db: 0 };
    }
    return transformedVolumeData.reduce((closest, dataPoint) => {
      if (!dataPoint || typeof dataPoint.timeMs === 'undefined') {
        return closest || { timeMs: targetTime, db: 0 };
      }
      if (!closest || typeof closest.timeMs === 'undefined') {
        return dataPoint;
      }
      return Math.abs(dataPoint.timeMs - targetTime) < Math.abs(closest.timeMs - targetTime)
        ? dataPoint
        : closest;
    }, transformedVolumeData[0] || { timeMs: targetTime, db: 0 });
  });

  const lastDataPoint = transformedVolumeData[transformedVolumeData.length - 1];
  if (lastDataPoint && !filteredVolumeData.some((data) => data.timeMs === lastDataPoint.timeMs)) {
    filteredVolumeData.push(lastDataPoint);
  }

  const formattedVolumeData = filteredVolumeData.map((dataPoint) => ({
    name: formatTime(dataPoint.timeMs),
    db: dataPoint.db,
  }));

  const paceDataString = audioData?.P_DP?.[5];
  const paceDataArray = paceDataString ? JSON.parse(paceDataString) : [];

  const transformedPaceData = paceDataArray.map((value, index) => ({
    name: 2.5 + index * 5,
    sps: value,
  }));
  if (!paceDataString) {
    console.warn("P_DP data is missing or invalid.");
  }

  const Sound_Plot = Array.isArray(audioData?.Sound_Plot) && audioData.Sound_Plot.length > 0
    ? audioData.Sound_Plot.map((value, index) => {
      const time = (index / 800);
      return {
        time: parseFloat(time.toFixed(2)),
        pitch: value,
      };
    })
    : [];

  const toneModulationData = audioData?.Tone_Modulation
    ? Object.entries(audioData.Tone_Modulation).map(([key, value]) => ({
      name: formatTime(parseFloat(key) * 1000),
      tone: value,
    }))
    : [];

  if (!audioData.Tone_Modulation) {
    console.warn("Tone_Modulation data is missing or invalid.");
  } else if (!Object.keys(audioData.Tone_Modulation).length) {
    console.warn("Tone_Modulation data is empty.");
  }

  const pauseDataString = audioData?.Pause_Distribution?.[5];
  const pauseDataArray = pauseDataString ? JSON.parse(pauseDataString) : [];
  const transformedPauseData = pauseDataArray.map((value, index) => ({
    name: 2.5 + index * 5,
    pause_percentage: value,
  }));
  if (!pauseDataString) {
    console.warn("Pause_Distribution data is missing or invalid.");
  }

  const data = activeTab === 'volume'
    ? formattedVolumeData
    : activeTab === 'pace'
      ? transformedPaceData
      : activeTab === 'pitch_Modulation'
        ? Sound_Plot
        : activeTab === 'pause_percentage'
          ? transformedPauseData
          : null;

  const dataKey = activeTab === 'volume'
    ? 'db'
    : activeTab === 'pace'
      ? 'sps'
      : activeTab === 'pitch_amplitude'
        ? "pitch"
        : activeTab === 'pause_percentage'
          ? 'pause_percentage'
          : null;

  const pauseValue = parseFloat(audioData.Pause_Percentage) || 0;
  const speechValue = 100 - pauseValue;

  const pausedata = {
    pause_value: pauseValue,
    speech_value: speechValue
  };
  // --- End of existing data processing ---

  // Add useEffect to handle activeTab change if 'filler_words' is active and mode becomes 'audio'
  useEffect(() => {
    if (mode === 'audio' && activeTab === 'filler_words') {
      setActiveTab('volume'); // Or any other default tab you prefer
    }
  }, [mode, activeTab]);

  const renderTabContent = (tabName) => {
    switch (tabName) {
      case "volume":
        return (
          <div className="h-full flex flex-col">
            <div className="flex flex-col md:flex-row w-full">
              <div className="w-full flex items-center justify-center">
                <AudioGraph
                  data={data}
                  dataKey={dataKey}
                  audioDuration={parseFloat(audioData.Audio_Duration) || 0}
                />
              </div>
              <div className="w-full md:w-1/3 flex items-center justify-center bg-gray-100 p-4">
                <SemiCircularProgress type="volume" value={parseFloat(audioData.Audio_Volume)} />
              </div>
            </div>
          </div>
        );

      case "pace":
        return (
          <div className="h-full flex flex-col">
            <div className="flex flex-col md:flex-row w-full">
              <div className="w-full flex items-center justify-center">
                <AudioGraph
                  data={data}
                  dataKey={dataKey}
                  audioDuration={parseFloat(audioData.Audio_Duration) || 0}
                />
              </div>
              <div className="w-full md:w-1/3 flex items-center justify-center bg-gray-100 p-4">
                <SemiCircularProgress type="pace" value={parseFloat(audioData.Pace)} />
              </div>
            </div>
          </div>
        );

      case 'pitch_Modulation':
        return (
          <div className="h-full flex flex-col">
            <div className="flex flex-col md:flex-row w-full">
              <div className="w-full flex items-center justify-center">
                <Pitchchart
                  data={Sound_Plot}
                  dataKey="pitch"
                  audioDuration={audioDuration}
                  pitchModulationDecision={audioData?.Pitch_Modulation?.decision}
                  Sound_Plot={audioData.Sound_Plot}
                  Tone_Modulation={audioData.Tone_Modulation_Array}
                  Tone_Modulation_Score={audioData.Tone_Modulation_Score}
                />
              </div>
              <div className="w-full md:w-1/4 flex items-center justify-center bg-gray-100 p-5 mt-5">
                <SemiCircularProgress
                  type="tone"
                  value={parseFloat(audioData.Tone_Modulation_Score) || parseFloat(audioData.Score) || 0}
                />
              </div>
            </div>
            {toneModulationData.length > 0 && (
              <div className="flex flex-col items-center justify-center mt-4">
                <span className="text-[#303972] font-semibold">
                </span>
              </div>
            )}
          </div>
        );

      case 'pause_percentage':
        return (
          <div className="h-full flex flex-col">
            <div className="flex lg:flex-row flex-col items-center h-[70%]">
              <AudioGraph
                data={data}
                dataKey={dataKey}
                audioDuration={parseFloat(audioData.Audio_Duration) || 0}
              />
              <div className="w-1/2 flex items-center justify-center">
                <DonutChart videoData={pausedata} colors={pauseColors} />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center h-[30%]">
              <div className="flex justify-around w-full text-[#767676] text-sm font-medium">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#00C49F]"></span>
                  Speech Percentage: <span className="text-[#333]">{pausedata.speech_value}%</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#FF8042]"></span>
                  Pause Percentage: <span className="text-[#333]">{pausedata.pause_value}%</span>
                </span>
              </div>
            </div>
          </div>
        );

      case 'filler_words':
        const hasFillerWords = textData?.transcription && textData.filler_words?.filler_words_index &&
                               Object.keys(textData.filler_words.filler_words_index).length > 0;

        return (
          <div className={`flex flex-col ${hasFillerWords ? 'max-h-[250px] overflow-y-auto' : ''}`}>
            {textData?.transcription ? (
              hasFillerWords ? (
                <HighlightedText
                  text={textData.transcription}
                  fillerWordsIndex={textData.filler_words.filler_words_index}
                />
              ) : (
                <p className="text-gray-600">There are no filler words.</p>
              )
            ) : (
              <p className="text-gray-600">No transcript available for filler word analysis.</p>
            )}
          </div>
        );

      case 'audio_duration':
        return (
          <div className="h-full flex flex-col">
            <div className="flex flex-row h-[70%]">
              <motion.div
                className="w-1/2 flex items-center justify-center bg-gray-100 p-4"
                animate={{ opacity: [0, 1], scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <div className="bg-blue-500 w-20 h-20 rounded-full animate-pulse"></div>
              </motion.div>
              <div className="w-1/2 flex items-center justify-center">
                {/* <Pie data={chartData} /> */}
              </div>
            </div>
          </div>
        );

      default:
        return <p className="p-4 text-center text-gray-600">Select a section to view details.</p>;
    }
  };

  const toggleTab = (tabName) => {
    setActiveTab(activeTab === tabName ? null : tabName);
  };

  const sections = [
    { id: "volume", title: "Volume" },
    { id: "pace", title: "Pace" },
    { id: "pause_percentage", title: "Pause Percentage" },
    { id: "pitch_Modulation", title: "Pitch Modulation" },
    // Only include 'filler_words' if mode is not 'audio'
    ...(mode !== 'audio' ? [{ id: "filler_words", title: "Filler Words" }] : []),
    { id: "audio_duration", title: "Audio Duration" },
  ];

  return (
    <div>
      {audioData.error ? (
        <div className='w-full h-[500px] flex items-center justify-center'>
          <div className="text-center h-full w-full flex flex-col items-center justify-center">
            <img
              src="/images/No_Search_Result.svg"
              alt="No results"
              className="mx-auto mb-4"
              style={{ width: "200px", height: "auto" }}
            />
            <p className="text-[#8A9ABE]">
              {audioData.error}
            </p>
          </div>
        </div>
      ) : (
        <div className="accordion">
          {sections.map((section) => (
            <div className="accordion-item" key={section.id}>
              <div
                className={`flex items-center justify-between p-4 ${
                  section.id !== "audio_duration" ? "cursor-pointer" : ""
                }`}
                onClick={() => section.id !== "audio_duration" && toggleTab(section.id)}
              >
                <span className="font-semibold text-[#303972] capitalize">
                  {section.title}
                  {/* Removed the filler words display from the title tab */}
                </span>

                {section.id === "audio_duration" ? (
                  <span className="font-semibold text-[#303972] text-lg flex flex-col items-start justify-start">
                    {audioData.Audio_Duration
                      ? `${Math.floor(audioData.Audio_Duration / 60)} : ${String(Math.round(audioData.Audio_Duration % 60)).padStart(2, "0")} `
                      : "N/A"}
                    <span className='text-sm pe-1 relative right-0 text-gray-500'>
                      M : S S
                    </span>
                  </span>

                ) : (
                  <div className="flex items-center">
                    <span className="ml-2 font-semibold text-[#303972]"></span>
                    <div className="ml-6 p-1 border border-gray-300 rounded-md flex items-center justify-center">
                      {activeTab === section.id ? (
                        <FaChevronUp className="text-[#303972]" />
                      ) : (
                        <FaChevronDown className="text-[#303972]" />
                      )}
                    </div>
                  </div>
                )}
              </div>

              {activeTab === section.id && section.id !== "audio_duration" && (
                <div className="border-t border-gray-300 p-4 bg-[#F7F7F7]">
                  {renderTabContent(section.id)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AudioAnalyticsContent;